import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';

import { useStream, useStreamSummary, useTopics } from 'src/api/queries';
import { PendingWork } from 'src/components/editorial/PendingWork';
import { Badge } from 'src/components/ui/Badge';
import { Icon } from 'src/components/ui/Icon';
import { WorkspaceBoundary } from 'src/components/ui/WorkspaceBoundary';
import { buildRailStages, type RailStage } from 'src/lib/streamBoard';
import type { Tone } from 'src/lib/tone';
import { formatDateTimeLong } from 'src/lib/time';
import type { EditorialStreamStatus } from 'src/types/editorial';

/**
 * Composition §2 — the canonical anatomy. The stream bar never disappears: losing track of which
 * broadcast you are in is the most expensive orientation failure in this product.
 */

/** Whatever the shell hands its workspaces (Composition §2b). */
export interface StreamRailContext {
  /** Mount point in the rail column, above the stage rail, for a workspace's "answer" regions —
      the Resumen's decisions, a queue note. Not sticky: it scrolls away, the stage rail stays. */
  railTop: HTMLDivElement | null;
}

const STATUS_TONE = {
  live: 'contradicted',
  reprocessing: 'partial',
  completed: 'supported',
  error: 'contradicted',
} satisfies Record<EditorialStreamStatus, Tone>;

const STATUS_LABEL = {
  live: 'en vivo',
  reprocessing: 'reprocesando',
  completed: 'completa',
  error: 'error',
} satisfies Record<EditorialStreamStatus, string>;

/** The broadcast landing view stays above the pipeline stages in the sticky rail. */
const TABS = [
  { to: '.', end: true, label: 'Resumen de captura', icon: 'dashboard' },
] as const;

const OTHER_SECTIONS = [
  { to: 'topics', label: 'Temas', key: 'topics', icon: 'tune' },
  { to: 'alertas', label: 'Alertas', key: 'alerts', icon: 'alerts' },
] as const;

/** Done flies a check on green; anything short of done says so with a dash; not-built has no flag. */
function standingClass(stage: RailStage): string {
  if (stage.standing === 'done') return 'stage-rail__item--done';
  if (stage.standing === 'not_built') return 'stage-rail__item--off';
  return 'stage-rail__item--idle';
}

export function StreamLayout() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const { pathname } = useLocation();
  const { data, isPending } = useStream(streamId);
  // The counts, in one request. This layout renders on every screen of a broadcast, so it was the
  // reason nine full collections were downloaded before anything appeared.
  const summary = useStreamSummary(streamId);
  const topics = useTopics(streamId);
  const [railTop, setRailTop] = useState<HTMLDivElement | null>(null);

  const stream = data?.stream;
  const stages = buildRailStages(summary.data?.summary ?? null);
  const pending = summary.isPending;
  const standing: Record<string, string> = {
    topics: topics.data?.topics ? `${topics.data.topics.payload.topics.length}` : '—',
    // No rules armed and no rule matched both read as nothing here, and they are the same
    // thing from this tab's point of view: this broadcast tripped nothing.
    alerts: summary.data?.summary.alertEvents ? `${summary.data.summary.alertEvents}` : '—',
  };
  const done: Record<string, boolean> = {
    topics: (topics.data?.topics?.payload.topics.length ?? 0) > 0,
    alerts: (summary.data?.summary.alertEvents ?? 0) > 0,
  };

  return (
    <>
      <header className="stream-bar">
        <Link className="stream-bar__back" to="/" data-nav>
          ‹ Transmisiones
        </Link>
        <div className="stream-bar__row">
          <div className="stream-bar__identity">
            <p className="stream-bar__eyebrow">
              Transmisión{stream ? ` · ${formatDateTimeLong(stream.createdAt)}` : ''}
            </p>
            {/* `||`, not `??`: the API sends an empty title for older streams, and an empty bar is
                the orientation failure this element exists to prevent. The slug keeps its text
                verbatim; the interface face (Sora) at page scale is a CSS decision, not a
                rewrite — machine text never wears the reading serif (Design System §1.3). */}
            <h1 className="stream-bar__title">{stream?.title || (isPending ? 'Cargando…' : streamId)}</h1>
            <div className="stream-bar__meta">
              {stream && <Badge tone={STATUS_TONE[stream.status]}>{STATUS_LABEL[stream.status]}</Badge>}
              {stream?.sourceUrl && (
                <a className="stream-bar__source" href={stream.sourceUrl} target="_blank" rel="noreferrer noopener">
                  Ver fuente ↗
                </a>
              )}
            </div>
          </div>
          <div className="stream-bar__aside">
            {summary.data && (
              <dl className="stream-bar__stats" aria-label="Resumen de la transmisión">
                <div className="stream-bar__stat">
                  <dd>{summary.data.summary.prenotas}</dd>
                  <dt>Historias</dt>
                </div>
                <div className="stream-bar__stat">
                  <dd>{summary.data.summary.turns}</dd>
                  <dt>Turnos</dt>
                </div>
                <div className="stream-bar__stat">
                  <dd>{summary.data.summary.claims}</dd>
                  <dt>Claims</dt>
                </div>
              </dl>
            )}
          </div>
        </div>
      </header>

      <div className="stream-layout">
        <main className="stream-layout__main">
          {/* Scoped to the workspace, so a broken screen never takes the bar and rail with it. */}
          <WorkspaceBoundary workspace={pathname}>
            <Outlet context={{ railTop } satisfies StreamRailContext} />
          </WorkspaceBoundary>
        </main>

        <aside className="stream-layout__rail">
          {/* Above the rail and deliberately not sticky: the pending-work note and a workspace's
              decision region are read once and then get out of the way. Only the stage rail stays
              pinned. */}
          <div className="stream-layout__rail-top" ref={setRailTop}>
            {/* Above the workspaces: the answer is the same on Temas, Claims, Relevancia and
                Historias, and an editor should not have to visit four empty screens to learn it
                once. */}
            <PendingWork work={summary.data?.summary.pendingWork ?? null} />
          </div>

          <nav className="stage-rail" aria-label="Etapas del proceso">
            <div className="stage-rail__tabs">
              {TABS.map(tab => (
                <NavLink
                  key={tab.label}
                  to={tab.to}
                  end={tab.end}
                  data-nav
                  className={({ isActive }) => `stage-rail__tab${isActive ? ' stage-rail__tab--active' : ''}`}
                >
                  <Icon name={tab.icon} />
                  {tab.label}
                </NavLink>
              ))}
            </div>

            <span className="stage-rail__label">Etapas del proceso</span>
            {stages.map(stage => {
              const body = (
                <>
                  <span className="stage-rail__no" aria-hidden="true">
                    {String(stage.step).padStart(2, '0')}
                  </span>
                  <span className="stage-rail__name">{stage.name}</span>
                  <span className="stage-rail__standing">{stage.detail}</span>
                  <span className="stage-rail__flag" aria-hidden="true">
                    {stage.standing === 'done' ? '✓' : '—'}
                  </span>
                </>
              );
              const className = ({ isActive }: { isActive: boolean }) =>
                `stage-rail__item${isActive ? ' stage-rail__item--active' : ''} ${standingClass(stage)}`;

              return stage.to ? (
                <NavLink key={stage.step} to={stage.to} data-nav title={`Ir a ${stage.name}`} className={className}>
                  {body}
                </NavLink>
              ) : (
                <span
                  key={stage.step}
                  className={`stage-rail__item stage-rail__item--inert ${standingClass(stage)}`}
                  aria-disabled="true"
                  title={stage.standing === 'not_built' ? 'Etapa no construida' : undefined}
                >
                  {body}
                </span>
              );
            })}

            <span className="stage-rail__label">Otras secciones</span>
            {OTHER_SECTIONS.map(section => (
              <NavLink
                key={section.label}
                to={section.to}
                data-nav
                className={({ isActive }) =>
                  `stage-rail__item stage-rail__item--other${isActive ? ' stage-rail__item--active' : ''} ${
                    done[section.key] ? 'stage-rail__item--done' : 'stage-rail__item--idle'
                  }`
                }
              >
                <Icon name={section.icon} />
                <span className="stage-rail__name">{section.label}</span>
                <span className="stage-rail__standing">{pending ? '·' : standing[section.key]}</span>
                <span className="stage-rail__flag" aria-hidden="true">
                  {done[section.key] ? '✓' : '—'}
                </span>
              </NavLink>
            ))}
          </nav>
        </aside>
      </div>
    </>
  );
}
